import { useReveal } from "@/lib/useReveal";
import { Smartphone, Stethoscope, ShieldCheck } from "lucide-react";

const categories = [
  {
    icon: Smartphone,
    title: "Patient Applications",
    body:
      "Consumer-grade experiences that put validated, personalised health intelligence in every pocket.",
    bullets: ["Longitudinal records", "Care navigation", "Health literacy"],
    image:
      "https://images.unsplash.com/photo-1758691462749-a95ce1bd7f96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkb2N0b3IlMjB0YWJsZXR8ZW58MHx8fHwxNzc4NjYzOTY2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: Stethoscope,
    title: "Practitioner Tools",
    body:
      "Clinically precise software for doctors, labs, and clinics, engineered to accelerate the right decision.",
    bullets: ["Structured workflows", "Lab integrations", "Decision support"],
    image:
      "https://images.unsplash.com/photo-1758691462774-f01ed567f2c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBkb2N0b3IlMjB0YWJsZXR8ZW58MHx8fHwxNzc4NjYzOTY2fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    icon: ShieldCheck,
    title: "Regulatory & Compliance",
    body:
      "A privacy-first foundation aligned with healthcare regulations, auditable, traceable, and quietly resilient.",
    bullets: ["DPDP-ready", "Audit trails", "Role-based access"],
    image:
      "https://images.pexels.com/photos/11288664/pexels-photo-11288664.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export default function Categories() {
  const ref = useReveal();
  return (
    <section
      id="categories"
      data-testid="categories-section"
      ref={ref}
      className="relative bg-[#E9ECEF] py-28 md:py-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-[#5F6368] reveal">
          <span className="inline-block h-px w-10 bg-[#D62828]" />
          Digital Health Categories
        </div>

        <h2 className="mt-8 max-w-4xl font-display text-[clamp(2.25rem,4.5vw,4.25rem)] leading-[1] tracking-[-0.035em] text-[#111111] reveal">
          Three layers, one continuous{" "}
          <span className="font-serif-italic italic text-[#D62828]">
            care fabric
          </span>
          .
        </h2>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6">
          {categories.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`category-${i}`}
                className="reveal group relative overflow-hidden rounded-3xl bg-white border border-[#CED4DA] hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(15,27,45,0.25)] transition-all duration-700"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-[1.4s] ease-out"
                  />
                </div>
                <div className="p-8 md:p-9">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-[#5F6368]">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#F8F9FA] text-[#D62828]">
                        <Icon size={14} />
                      </span>
                      0{i + 1}
                    </span>
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D62828]" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl md:text-3xl tracking-tight text-[#111111]">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-[1.65] text-[#5F6368]">
                    {c.body}
                  </p>
                  <ul className="mt-6 space-y-2">
                    {c.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-center gap-3 text-[13px] text-[#111111]"
                      >
                        <span className="h-1 w-1 rounded-full bg-[#D62828]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
